/**
 * Wall (城墙) - Chinese-style fortress wall segments
 * Features: Stone base, gray brick walls, crenellated battlements
 * Variants: single (pillar), end, straight, corner, tee, cross
 */

import * as THREE from 'three';
import type { WallVariant } from '@/game/components/Wall';

// Shared materials
const stoneMat = new THREE.MeshStandardMaterial({ color: 0x6a6a6a, roughness: 0.9 });
const darkStoneMat = new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.95 });
const lightStoneMat = new THREE.MeshStandardMaterial({ color: 0x7a7a7a, roughness: 0.85 });

// Wall dimensions
const WALL_HEIGHT = 1.2;
const WALL_THICKNESS = 0.3;
const PILLAR_SIZE = 0.4;
const BATTLEMENT_HEIGHT = 0.25;
const BATTLEMENT_WIDTH = 0.15;
const BASE_HEIGHT = 0.15;

/**
 * Create a single pillar (used at corners and ends)
 */
function createPillar(group: THREE.Group, x: number, z: number): void {
  // Base
  const baseGeom = new THREE.BoxGeometry(PILLAR_SIZE + 0.1, BASE_HEIGHT, PILLAR_SIZE + 0.1);
  const base = new THREE.Mesh(baseGeom, darkStoneMat);
  base.position.set(x, BASE_HEIGHT / 2, z);
  base.castShadow = true;
  base.receiveShadow = true;
  group.add(base);

  // Main pillar
  const pillarGeom = new THREE.BoxGeometry(PILLAR_SIZE, WALL_HEIGHT, PILLAR_SIZE);
  const pillar = new THREE.Mesh(pillarGeom, stoneMat);
  pillar.position.set(x, BASE_HEIGHT + WALL_HEIGHT / 2, z);
  pillar.castShadow = true;
  group.add(pillar);

  // Pillar cap
  const capGeom = new THREE.BoxGeometry(PILLAR_SIZE + 0.08, 0.1, PILLAR_SIZE + 0.08);
  const cap = new THREE.Mesh(capGeom, darkStoneMat);
  cap.position.set(x, BASE_HEIGHT + WALL_HEIGHT + 0.05, z);
  cap.castShadow = true;
  group.add(cap);

  // Top battlement on pillar
  const battleGeom = new THREE.BoxGeometry(BATTLEMENT_WIDTH, BATTLEMENT_HEIGHT, BATTLEMENT_WIDTH);
  const battle = new THREE.Mesh(battleGeom, lightStoneMat);
  battle.position.set(x, BASE_HEIGHT + WALL_HEIGHT + 0.1 + BATTLEMENT_HEIGHT / 2, z);
  battle.castShadow = true;
  group.add(battle);
}

/**
 * Create a wall segment between two points
 */
function createWallSegment(
  group: THREE.Group,
  startX: number,
  startZ: number,
  endX: number,
  endZ: number
): void {
  const dx = endX - startX;
  const dz = endZ - startZ;
  const length = Math.sqrt(dx * dx + dz * dz);
  const angle = Math.atan2(dx, dz);

  const centerX = (startX + endX) / 2;
  const centerZ = (startZ + endZ) / 2;

  // Base
  const baseGeom = new THREE.BoxGeometry(WALL_THICKNESS + 0.1, BASE_HEIGHT, length);
  const base = new THREE.Mesh(baseGeom, darkStoneMat);
  base.position.set(centerX, BASE_HEIGHT / 2, centerZ);
  base.rotation.y = angle;
  base.castShadow = true;
  base.receiveShadow = true;
  group.add(base);

  // Main wall body
  const wallGeom = new THREE.BoxGeometry(WALL_THICKNESS, WALL_HEIGHT - 0.1, length);
  const wall = new THREE.Mesh(wallGeom, stoneMat);
  wall.position.set(centerX, BASE_HEIGHT + (WALL_HEIGHT - 0.1) / 2, centerZ);
  wall.rotation.y = angle;
  wall.castShadow = true;
  group.add(wall);

  // Wall top platform
  const topGeom = new THREE.BoxGeometry(WALL_THICKNESS + 0.1, 0.08, length);
  const top = new THREE.Mesh(topGeom, darkStoneMat);
  top.position.set(centerX, BASE_HEIGHT + WALL_HEIGHT - 0.04, centerZ);
  top.rotation.y = angle;
  group.add(top);

  // Battlements (crenellations)
  const numBattlements = Math.max(2, Math.floor(length / 0.25));
  const spacing = length / numBattlements;
  const battleGeom = new THREE.BoxGeometry(BATTLEMENT_WIDTH, BATTLEMENT_HEIGHT, BATTLEMENT_WIDTH);

  for (let i = 0; i < numBattlements; i++) {
    const t = (i + 0.5) / numBattlements - 0.5;
    const bx = centerX + Math.sin(angle) * t * length;
    const bz = centerZ + Math.cos(angle) * t * length;

    const battle = new THREE.Mesh(battleGeom, lightStoneMat);
    battle.position.set(bx, BASE_HEIGHT + WALL_HEIGHT + BATTLEMENT_HEIGHT / 2, bz);
    battle.castShadow = true;
    group.add(battle);
  }
}

/**
 * Create single wall variant (isolated pillar)
 */
function createSingleWall(group: THREE.Group): void {
  createPillar(group, 0, 0);
}

/**
 * Create end wall variant (wall segment ending in pillar)
 * Default orientation: connects to North (+Z)
 */
function createEndWall(group: THREE.Group): void {
  // Pillar at center
  createPillar(group, 0, 0);

  // Wall segment going North
  createWallSegment(group, 0, 0, 0, 0.4);
}

/**
 * Create straight wall variant (wall connecting two sides)
 * Default orientation: North-South
 */
function createStraightWall(group: THREE.Group): void {
  // Wall segment going through center
  createWallSegment(group, 0, -0.5, 0, 0.5);
}

/**
 * Create corner wall variant (L-shaped)
 * Default orientation: North and East
 */
function createCornerWall(group: THREE.Group): void {
  // Corner pillar
  createPillar(group, 0, 0);

  // Wall segment going North
  createWallSegment(group, 0, 0, 0, 0.4);

  // Wall segment going East
  createWallSegment(group, 0, 0, 0.4, 0);
}

/**
 * Create tee wall variant (T-shaped)
 * Default orientation: North, East, West
 */
function createTeeWall(group: THREE.Group): void {
  // Center pillar
  createPillar(group, 0, 0);

  // Wall segment going North
  createWallSegment(group, 0, 0, 0, 0.4);

  // Wall segment going East
  createWallSegment(group, 0, 0, 0.4, 0);

  // Wall segment going West
  createWallSegment(group, 0, 0, -0.4, 0);
}

/**
 * Create cross wall variant (4-way intersection)
 */
function createCrossWall(group: THREE.Group): void {
  // Center pillar
  createPillar(group, 0, 0);

  // Wall segments in all 4 directions
  createWallSegment(group, 0, 0, 0, 0.4);   // North
  createWallSegment(group, 0, 0, 0.4, 0);   // East
  createWallSegment(group, 0, 0, 0, -0.4);  // South
  createWallSegment(group, 0, 0, -0.4, 0);  // West
}

/**
 * Main wall mesh creation function
 */
export function createWallMesh(
  group: THREE.Group,
  mainColor: THREE.Color,
  accentColor: THREE.Color,
  variant: WallVariant = 'single',
  rotation: number = 0
): void {
  // Create a sub-group for rotation
  const wallGroup = new THREE.Group();

  switch (variant) {
    case 'single':
      createSingleWall(wallGroup);
      break;
    case 'end':
      createEndWall(wallGroup);
      break;
    case 'straight':
      createStraightWall(wallGroup);
      break;
    case 'corner':
      createCornerWall(wallGroup);
      break;
    case 'tee':
      createTeeWall(wallGroup);
      break;
    case 'cross':
      createCrossWall(wallGroup);
      break;
    default:
      createSingleWall(wallGroup);
  }

  // Apply rotation
  wallGroup.rotation.y = rotation;

  group.add(wallGroup);
}
